import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { API } from '@/App';

const APP_URL = 'https://mallorcaklan.com';
const QR_URL = `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(APP_URL + '/descargar')}&bgcolor=1a1a1a&color=d4af37&format=svg`;

function detectDevice() {
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod/.test(ua)) return 'ios';
  if (/Android/.test(ua)) return 'android';
  return 'desktop';
}

const STEPS = {
  android: [
    { num: '1', text: 'Abre esta pagina en Chrome', detail: 'Si estas en otra app, copia el enlace y abrelo en Chrome' },
    { num: '2', text: 'Toca los 3 puntos de arriba a la derecha', detail: 'Se abrira el menu de Chrome' },
    { num: '3', text: 'Selecciona "Instalar aplicacion"', detail: 'O "Agregar a pantalla de inicio" segun tu version' },
    { num: '4', text: 'Confirma la instalacion', detail: 'MK aparecera como una app mas en tu movil' },
  ],
  ios: [
    { num: '1', text: 'Abre esta pagina en Safari', detail: 'Importante: debe ser Safari, no Chrome ni otro navegador' },
    { num: '2', text: 'Toca el icono de Compartir', detail: 'Es el cuadrado con la flecha hacia arriba, abajo en la barra' },
    { num: '3', text: 'Busca "Agregar a pantalla de inicio"', detail: 'Desliza hacia abajo en el menu hasta encontrarlo' },
    { num: '4', text: 'Toca "Agregar"', detail: 'MK aparecera en tu pantalla de inicio como cualquier app' },
  ],
  desktop: [
    { num: '1', text: 'Abre esta pagina en Chrome', detail: 'Funciona mejor con Google Chrome' },
    { num: '2', text: 'Busca el icono de instalacion', detail: 'En la barra de direcciones aparece un icono de descarga o un "+"' },
    { num: '3', text: 'Haz clic en "Instalar"', detail: 'MK se instalara como una aplicacion de escritorio' },
  ],
};

function StepCard({ step, accent }) {
  return (
    <div className="flex gap-3 items-start" data-testid={`step-${step.num}`}>
      <div style={{
        width: '32px', height: '32px', borderRadius: '50%', flexShrink: 0,
        background: 'linear-gradient(135deg, #d4af37, #b8941f)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: '#000', fontWeight: '800', fontSize: '14px'
      }}>{step.num}</div>
      <div>
        <p style={{ color: '#e0e0e0', fontWeight: '600', fontSize: '15px' }}>{step.text}</p>
        <p style={{ color: '#777', fontSize: '12px', marginTop: '2px' }}>{step.detail}</p>
      </div>
    </div>
  );
}

export default function Descargar() {
  const [device, setDevice] = useState('android');
  const [showQR, setShowQR] = useState(false);

  useEffect(() => {
    setDevice(detectDevice());
    // Log visit
    fetch(`${API}/descargar/visit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ referrer: document.referrer })
    }).catch(() => {});
  }, []);

  const steps = STEPS[device] || STEPS.android;

  return (
    <div data-testid="descargar-page" style={{
      minHeight: '100vh', background: '#0d0d0d',
      display: 'flex', flexDirection: 'column', alignItems: 'center'
    }}>
      {/* Hero */}
      <div style={{
        width: '100%', padding: '40px 20px 30px', textAlign: 'center',
        background: 'linear-gradient(180deg, rgba(26,58,46,0.4) 0%, transparent 100%)'
      }}>
        <img
          src="https://customer-assets.emergentagent.com/job_espanol-mode-15/artifacts/jlomgnz6_WhatsApp%20Image%202025-11-16%20at%2010.07.15.jpeg"
          alt="Mallorca Klan" style={{ height: '80px', margin: '0 auto 16px', borderRadius: '16px' }}
        />
        <h1 style={{ fontSize: '28px', fontWeight: '800', color: '#d4af37', margin: '0 0 8px', letterSpacing: '-0.5px' }}>
          MALLORCA KLAN
        </h1>
        <p style={{ color: '#b8941f', fontSize: '13px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '2px', margin: '0 0 16px' }}>
          Forja tu leyenda
        </p>
        <p style={{ color: '#999', fontSize: '15px', maxWidth: '400px', margin: '0 auto', lineHeight: '1.5' }}>
          Los guerreros no nacen, se forjan. Cada dia es una batalla, cada entreno una victoria.
          Unete al Klan y conquista tu mejor version.
        </p>
      </div>

      {/* Main content */}
      <div style={{ width: '100%', maxWidth: '440px', padding: '0 20px 40px' }}>

        {/* Device selector */}
        <div style={{ display: 'flex', gap: '6px', marginBottom: '24px', justifyContent: 'center' }}>
          {[
            { key: 'android', label: 'Android' },
            { key: 'ios', label: 'iPhone' },
            { key: 'desktop', label: 'PC' },
          ].map(d => (
            <button key={d.key} onClick={() => setDevice(d.key)} data-testid={`device-${d.key}`} style={{
              padding: '8px 16px', borderRadius: '20px', fontSize: '13px', fontWeight: '600', cursor: 'pointer',
              border: device === d.key ? '1px solid #d4af37' : '1px solid #333',
              background: device === d.key ? 'rgba(212,175,55,0.15)' : 'transparent',
              color: device === d.key ? '#d4af37' : '#666',
              transition: 'all 0.2s'
            }}>{d.label}</button>
          ))}
        </div>

        {/* Steps */}
        <div style={{
          background: 'rgba(255,255,255,0.03)', borderRadius: '16px', padding: '20px',
          border: '1px solid rgba(255,255,255,0.06)', marginBottom: '24px'
        }}>
          <p style={{ color: '#d4af37', fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '16px' }}>
            {device === 'ios' ? 'Instalar en iPhone' : device === 'android' ? 'Instalar en Android' : 'Instalar en PC'}
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {steps.map(s => <StepCard key={s.num} step={s} />)}
          </div>
        </div>

        {/* CTA Button */}
        <a href={APP_URL} data-testid="btn-open-app" style={{
          display: 'block', width: '100%', padding: '14px', borderRadius: '12px', textAlign: 'center',
          background: 'linear-gradient(135deg, #d4af37, #b8941f)', color: '#000', fontWeight: '800',
          fontSize: '16px', textDecoration: 'none', letterSpacing: '0.5px',
          boxShadow: '0 4px 20px rgba(212,175,55,0.3)'
        }}>
          ABRIR MALLORCA KLAN
        </a>

        {/* Features */}
        <div style={{
          display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginTop: '24px'
        }}>
          {[
            { icon: '&#9876;', label: 'Entrenos', desc: 'Rutinas personalizadas' },
            { icon: '&#9878;', label: 'Nutricion', desc: 'Planes alimenticios' },
            { icon: '&#10026;', label: 'Comunidad', desc: 'El Klan te respalda' },
            { icon: '&#9733;', label: 'Progreso', desc: 'Registra tu evolucion' },
          ].map((f, i) => (
            <div key={i} style={{
              background: 'rgba(255,255,255,0.02)', borderRadius: '12px', padding: '14px',
              border: '1px solid rgba(255,255,255,0.05)', textAlign: 'center'
            }}>
              <div style={{ fontSize: '22px', marginBottom: '4px' }} dangerouslySetInnerHTML={{ __html: f.icon }} />
              <p style={{ color: '#ccc', fontSize: '13px', fontWeight: '600' }}>{f.label}</p>
              <p style={{ color: '#555', fontSize: '11px' }}>{f.desc}</p>
            </div>
          ))}
        </div>

        {/* QR Section */}
        <div style={{ textAlign: 'center', marginTop: '28px' }}>
          <button onClick={() => setShowQR(!showQR)} data-testid="btn-toggle-qr" style={{
            background: 'transparent', border: '1px solid #333', borderRadius: '10px',
            padding: '10px 20px', color: '#888', fontSize: '13px', cursor: 'pointer'
          }}>
            {showQR ? 'Ocultar QR' : 'Mostrar codigo QR para compartir'}
          </button>
          {showQR && (
            <div style={{ marginTop: '16px' }} data-testid="qr-section">
              <div style={{
                background: '#1a1a1a', borderRadius: '16px', padding: '16px', display: 'inline-block',
                border: '1px solid #333'
              }}>
                <img src={QR_URL} alt="QR Mallorca Klan" style={{ width: '180px', height: '180px' }} data-testid="qr-image" />
              </div>
              <p style={{ color: '#555', fontSize: '11px', marginTop: '8px' }}>
                Escanea para descargar MK
              </p>
              <p style={{ color: '#444', fontSize: '10px', marginTop: '4px', fontFamily: 'monospace' }}>
                mallorcaklan.com/descargar
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ textAlign: 'center', marginTop: '32px', paddingTop: '20px', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
          <p style={{ color: '#444', fontSize: '11px' }}>
            Mallorca Klan &middot; Forjando guerreros desde Mallorca
          </p>
          <Link to="/login" style={{ color: '#555', fontSize: '11px', textDecoration: 'none', marginTop: '4px', display: 'inline-block' }}>
            Ya tengo cuenta → Iniciar sesion
          </Link>
        </div>
      </div>
    </div>
  );
}
